import { createActivityTitle } from "~/ui/activitytitle";
import { createActivityItem, IActivityItemOptions } from "~/ui/activityitem";
import { createPhotoPreview } from "~/ui/photopreview";
import { EDashboardIconType } from "~/ui/dashboardicon";
import { createDashboardItem } from "~/ui/dashboarditem";
import StarsBanner from "~/ui/stars-banner";
import { v4 as uuidv4 } from "uuid";
import { createPOIListImage } from "~/ui/poilistimage";
import { node, icon } from "~/util/dom";
import { PAGE_SUGGEST } from "~/mymaps2/reviews/constants";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import { store } from "~/state/store";
import { unsetDetail } from "~/state/detail/state";
import { ROUTE_CONST } from "~/route/const";
import { detail, getIcon } from "./providers/poiagg";
import { standardDistanceFormat, standardTimeFormat } from "~/util/format";
import createSDSIcon from "~/ui/icon";
import { activityTypeToIcon } from "~/activities/util";
import { timeWithUserSpeed } from "~/util/user-speed";

const DASHBOARD_URL = "/mymaps/v1/dashboard";
const PROFILE_URL = "/mymaps/v1/profile";

function routeTypeToIcon(routeType: number): string {
	switch (routeType) {
		// kolo
		case 121:
		case 122:
		case 123:
		case 124:
		case 125:
			return "bike";

		// pesky
		case 131:
		case 132:
		case 133:
		case 134:
			return "walk";

		// lyze
		case 141:
			return "ski-cross-country";

		case 143:
		case 144:
			return "boat";

		case 200:
			return "pubt";

		default:
			// [111, 112, 113, 114]
			return "car";
	}
}

function placesOptions(data: any): Partial<IActivityItemOptions> {
	const commonOptions = {
		href: `https://${CONFIG.BASE_URL}/?dim=${data.id}`,
		icon: icon("star-line"),
		title: data.title,
		middleHTML: Mapy.Util.escapeTags(data.title),
	};
	let options: Partial<IActivityItemOptions> = {};

	switch (data.type) {
		case "entity":
		case "point":
			if ((data.type === "point" && data.mark) || (data.type === "entity" && data.source && data.source_id)) {
				const routeButton = node("button", { type: "button", className: "dashboard__activityitem__routebutton", title: _("action.route.title") });

				routeButton.setAttribute("data-log-text", "dashboardItemRoute");
				routeButton.setAttribute("data-log-action", "click");
				routeButton.appendChild(icon("navigate"));

				routeButton.addEventListener("click", async event => {
					event.stopPropagation();
					event.preventDefault();

					try {
						const { poi } = await detail(data.type === "point" ? "coor" : data.source, data.type === "point" ? `${data.mark.lon},${data.mark.lat}` : data.source_id, { lang: CONFIG.langPriority }).promise;
						const route = Mapy.getComponent("route");

						await route.addItem({
							coords: SMap.Coords.fromWGS84(poi.mark.lon, poi.mark.lat),
							position: ROUTE_CONST.position.end,
							name: poi.title,
							source: poi.source,
							id: ["coor", "closure"].indexOf(poi.source) === -1 ? parseFloat(poi.id.toString()) : poi.id,
							desc: poi.description || poi.title,
						});
					} catch (ex) {
						// eslint-disable-next-line no-console
						console.log(ex);
						// kdyz se nepovede nacist POI, tise vyhnijeme
					}
				});
				options = {
					...commonOptions,
					icon: data.thumbnailUrl
						? node("img", { src: data.thumbnailUrl.replace("{width}", "80").replace("{height}", "80"), className: "thumbnail" })
						: data.typeId
							? node("img", { src: getIcon(data.typeId, { scale: 2 }), className: "icon" })
							: icon("star-line"),
					buttons: routeButton,
				};
			}
			break;

		case "route":
			options = {
				...commonOptions,
				icon: icon(routeTypeToIcon(data.routeType)),
				middleHTML: _("mymap.places.route", { DIST: `${standardDistanceFormat(data.length).string}`, TIME: standardTimeFormat(timeWithUserSpeed(data.duration, data.routeType), true).string }),
			};
			break;

		case "measure":
			// TODO: ay bude v datech RO priznak, tak podle toho dat jinou ikonu
			options = {
				...commonOptions,
				icon: data.readonly ? icon("custom-distance") : icon("ruler"),
			};
			break;

		case "path":
		case "measureRO":
			options = {
				...commonOptions,
				icon: icon("custom-distance"),
			};
			break;

		case "set":
			options = {
				...commonOptions,
				icon: icon("custom-points"),
				middleHTML: _("userMarks.favouritePoint", {
					NUM: data.count,
					TXT: data.count,
				}),
			};
			break;

		default:
			options = { ...commonOptions };
			break;
	}

	return options;
}

export function createDashboardList(data: any, reload: () => void = () => {}, isCurrentUser = false): DocumentFragment {
	const list = document.createDocumentFragment();
	const empties: Array<HTMLElement> = [];

	// mista a trasy
	if (data?.saved_entities?.last?.length) {
		const section = node("section", { className: "dashboard-section" });

		section.appendChild(createActivityTitle({
			innerHTML: _("mymap.dashboard.lastPlaces"),
			iconType: EDashboardIconType.PLACES,
		}));
		list.appendChild(section);

		const placesList = node("ul", { className: "dashboard-placeslist" });

		data.saved_entities.last.forEach(place => {
			const li = node("li");
			const item = createActivityItem(placesOptions(place));

			li.appendChild(item);
			item.addEventListener("click", event => {
				if (!Mapy.Util.linkToNewWindow(event)) {
					event.preventDefault();
					Mapy.getComponent("dimcurator").setState({
						dim: place.id,
						backTitle: isCurrentUser ? _("mymap.stateswitch") : _("userProfile.userContributions"),
						backAction: () => {
							Mapy.getComponent("dimcurator").setState();
							store.dispatch(unsetDetail());
							Mapy.getComponent("path").deactivate();
							Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("dashboard"));
							Mapy.getComponent("history").save();
						},
						onClose: reload,
					}).loadData().then(() => {
						Mapy.getComponent("history").save();
					}, () => {});
				}
			});
			item.setAttribute("data-log-text", "dashboardInfo");
			item.setAttribute("data-log-type", "places");
			placesList.appendChild(li);
		});
		section.appendChild(placesList);
	} else {
		const section = node("section", { className: "dashboard-section" });

		section.appendChild(createActivityTitle({
			innerHTML: _("mymap.dashboard.tipPlaces"),
			iconType: EDashboardIconType.PLACES,
		}));
		empties.push(section);
	}

	// aktivity
	if (data?.saved_tracks?.last?.length && data.saved_tracks.last.filter(activity => !!activity).length) {
		const section = node("section", { className: "dashboard-section" });

		section.appendChild(createActivityTitle({
			innerHTML: _("mymap.dashboard.lastActivities"),
			iconType: EDashboardIconType.ACTIVITIES,
		}));
		list.appendChild(section);

		const tracksList = node("ul", { className: "dashboard-activitieslist" });

		data.saved_tracks.last.forEach(activity => {
			if (!activity) {
				return;
			}

			const li = node("li");
			const distance = standardDistanceFormat(activity.length);
			const formatTime = standardTimeFormat(activity.duration, true);
			const item = createActivityItem({
				icon: createSDSIcon({ symbol: activityTypeToIcon(activity.trackType) }),
				title: activity.title,
				middleHTML: `${distance.distance} ${distance.unit} &bull; ${formatTime.time} ${formatTime.unit}`,
				href: `https://${CONFIG.BASE_URL}/?moje-mapy&cat=aktivity&mid=${activity.id}`,
			});

			li.appendChild(item);
			item.addEventListener("click", event => {
				if (!Mapy.Util.linkToNewWindow(event)) {
					event.preventDefault();
					Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("activities"));
					Mapy.getComponent("activities").state = {
						mid: activity.id,
						backTitle: isCurrentUser ? _("mymap.stateswitch") : _("userProfile.userContributions"),
						backAction: () => {
							Mapy.getComponent("activities").state = {};
							Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("dashboard"));
							Mapy.getComponent("history").save();
						},
						onClose: reload,
					};
				}
			});
			item.setAttribute("data-log-text", "dashboardInfo");
			item.setAttribute("data-log-type", "activities");
			tracksList.appendChild(li);
		});
		section.appendChild(tracksList);
	} else {
		const section = node("section", { className: "dashboard-section" });

		section.appendChild(createActivityTitle({
			innerHTML: _("mymap.dashboard.tipActivities"),
			iconType: EDashboardIconType.ACTIVITIES,
		}));
		empties.push(section);
	}

	// hodnoceni
	if (data?.saved_reviews?.last?.length) {
		const section = node("section", { className: "dashboard-section" });

		section.appendChild(createActivityTitle({
			innerHTML: isCurrentUser ? _("mymap.dashboard.lastReviews") : _("mymap.dashboard.lastPublicReviews"),
			iconType: EDashboardIconType.REVIEWS,
		}));
		list.appendChild(section);

		const reviewsList = node("ul", { className: "dashboard-reviewslist" });

		data.saved_reviews.last.forEach(review => {
			const li = node("li");
			const rating = new StarsBanner({
				percentage: review.rating < 1 ? review.rating * 100 : review.rating,
			});
			const middle = node("div", { innerHTML: `<div>${review.description.titleVars.titleSmartMain}</div>` });

			middle.appendChild(rating.container);

			const item = createActivityItem({
				icon: createPOIListImage(review.description),
				title: review.description.titleVars.titleSmartMain,
				middleHTML: middle.innerHTML,
				href: `https://${CONFIG.BASE_URL}/?moje-mapy&cat=reviews&mid=${review.id}`,
			});

			li.appendChild(item);
			item.addEventListener("click", event => {
				if (!Mapy.Util.linkToNewWindow(event)) {
					event.preventDefault();
					Mapy.getComponent("detail").setState({
						zoomToReviews: true,
						source: review?.detailPoi?.source,
						id: review?.detailPoi?.id,
						backTitle: _("mymap.stateswitch"),
					}).then(() => {
						Mapy.getComponent("history").save();
					}, () => {});
				}
			});
			item.setAttribute("data-log-text", "dashboardInfo");
			item.setAttribute("data-log-type", "reviews");
			!isCurrentUser && item.setAttribute("data-log-publicProfile", "1");
			reviewsList.appendChild(li);
		});
		section.appendChild(reviewsList);
	} else {
		const section = node("section", { className: "dashboard-section" });

		section.appendChild(createActivityTitle({
			innerHTML: _("mymap.dashboard.tipReviews"),
			iconType: EDashboardIconType.REVIEWS,
		}));
		empties.push(section);
	}

	// fotky
	if (data?.uploaded_photos?.last?.length) {
		const section = node("section", { className: "dashboard-section dashboard-photos" });

		section.appendChild(createActivityTitle({
			innerHTML: isCurrentUser ? _("mymap.dashboard.lastPhotos") : _("mymap.dashboard.lastPublicPhotos"),
			iconType: EDashboardIconType.PHOTOS,
		}));
		list.appendChild(section);

		const photosList = node("ul", { className: "dashboard-photopreviewlist" });

		data.uploaded_photos.last.forEach(photo => {
			const li = node("li");
			const item = createPhotoPreview({
				src: photo.urls.default.replace("{width}", "80").replace("{height}", "80"),
				className: "dashboard-photopreview",
				href: `https://${CONFIG.BASE_URL}/?source=foto&id=${photo.id}`,
			});

			li.appendChild(item);
			item.addEventListener("click", event => {
				if (!Mapy.Util.linkToNewWindow(event)) {
					event.preventDefault();
					Mapy.getComponent("photoDetail").setData({
						source: photo.mediaSource === "mapy" ? "foto" : "ffoto",
						id: photo.id,
						galleryType: "myMaps",
						backTitle: isCurrentUser ? _("mymap.stateswitch") : _("userProfile.userContributions"),
						onClose: () => {
							reload && reload();
						},
					}).activate();
					Mapy.getComponent("history").save();
				}
			});
			item.setAttribute("data-log-text", "dashboardInfo");
			item.setAttribute("data-log-type", "photos");
			!isCurrentUser && item.setAttribute("data-log-publicProfile", "1");
			photosList.appendChild(li);
		});
		section.appendChild(photosList);
	} else {
		const section = node("section", { className: "dashboard-section" });

		section.appendChild(createActivityTitle({
			innerHTML: _("mymap.dashboard.tipPhotos"),
			iconType: EDashboardIconType.PHOTOS,
		}));
		empties.push(section);
	}

	if (isCurrentUser) {
		// z prazdnych ukazeme maximalne 2 nahodne tipy
		empties.sort(() => Math.random() > 0.5 ? 1 : -1).slice(0, 2).forEach(section => {
			list.appendChild(section);
		});
	}

	return list;
}

export function createDashboardSections({
	data = {},
	isCurrentUser,
	userPublicId,
}: {
	data?: any;
	isCurrentUser: boolean;
	userPublicId: string;
}): DocumentFragment {
	const sections = document.createDocumentFragment();

	// mista a trasy
	if (isCurrentUser) {
		const places = createDashboardItem({
			iconType: EDashboardIconType.PLACES,
			titleHTML: _("mymap.switcher.saved"),
			subtitleHTML: (data?.saved_entities?.placesCount || 0) + (data?.saved_entities?.routesCount || 0),
			href: `https://${CONFIG.BASE_URL}/?moje-mapy&cat=mista-trasy`,
		});

		sections.appendChild(places);
		places.addEventListener("click", event => {
			event.preventDefault();
			Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("places"));
			Mapy.getComponent("history").save();
		});
		places.setAttribute("data-log-text", "places");
	}

	// aktivity
	if (isCurrentUser) {
		const activities = createDashboardItem({
			iconType: EDashboardIconType.ACTIVITIES,
			titleHTML: _("mymap.switcher.activities"),
			subtitleHTML: data?.saved_tracks?.tracksCount || 0,
			href: `https://${CONFIG.BASE_URL}/?moje-mapy&cat=aktivity`,
		});

		sections.appendChild(activities);
		activities.addEventListener("click", event => {
			event.preventDefault();
			Mapy.getComponent("activities").state = {};
			Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("activities"));
			Mapy.getComponent("history").save();
		});
		activities.setAttribute("data-log-text", "activities");
	}

	// hodnoceni
	const reviews = createDashboardItem({
		iconType: EDashboardIconType.REVIEWS,
		titleHTML: _("mymap.switcher.reviews"),
		subtitleHTML: (data?.saved_reviews?.reviewCounts?.ok || 0)
			+ (isCurrentUser
				? ` &bull; ${_("mymap.dashboard.reviews.subtitleProposals2", {
					proposalsCount: data?.reviews_proposal?.proposalCount || 0,
					newProposalsCount: data?.reviews_proposal?.newProposalCount || 0,
				})}`
				: "")
			+ (data?.saved_reviews?.reviewCounts?.rejected > 0 ? `<span class="rejected">! ${data.saved_reviews.reviewCounts.rejected}</span>` : ""),
		href: `https://${CONFIG.BASE_URL}${isCurrentUser ? "/?moje-mapy&cat=reviews" : `/?up=${userPublicId}&upc=reviews`}`,
	});

	sections.appendChild(reviews);
	reviews.addEventListener("click", event => {
		event.preventDefault();
		if (isCurrentUser) {
			if (data?.reviews_proposal?.newProposalCount) {
				Mapy.getComponent("reviews").state = {
					activePage: PAGE_SUGGEST,
				};
			}
			Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("reviews"));
		} else {
			Mapy.getComponent("userProfile").setState({
				up: userPublicId,
				upc: "reviews",
				direct: false,
			});
		}
		setTimeout(() => {
			Mapy.getComponent("history").save();
		}, 0);
	});
	reviews.setAttribute("data-log-text", "reviews");
	!isCurrentUser && reviews.setAttribute("data-log-publicProfile", "1");
	// analytika, pokud ma navrhy na hodnoceni a proklikl se na ne z dashboardu
	if (isCurrentUser && data?.reviews_proposal?.newProposalCount) {
		reviews.setAttribute("data-log-type", "notified");
	}

	// fotky
	const photos = createDashboardItem({
		iconType: EDashboardIconType.PHOTOS,
		titleHTML: _("mymap.switcher.photos"),
		subtitleHTML: data?.uploaded_photos?.totalCount || 0,
		href: `https://${CONFIG.BASE_URL}/?source=author&id=${userPublicId}`,
	});

	sections.appendChild(photos);
	photos.setAttribute("data-log-text", "photos");
	!isCurrentUser && photos.setAttribute("data-log-publicProfile", "1");

	photos.addEventListener("click", event => {
		event.preventDefault();
		Mapy.getComponent("gallery").activate().setData({
			source: "author",
			id: userPublicId,
		});

		Mapy.getComponent("history").save();
	});

	return sections;
}

export function fetchDashboard(userPublicId: string) {
	return fetch(`${CONFIG.requestUrl}${DASHBOARD_URL}/${userPublicId}`, {
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
	});
}

export function fetchUserIcon(userPublicId: string) {
	return fetch(`${CONFIG.requestUrl}${PROFILE_URL}/${userPublicId}/icon`, {
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
	});
}
